import type { ReactNode } from "react";
import { useScrollHero } from "../lib/useScrollHero";
import { Container } from "./Container";
import { DisplayHeading } from "./DisplayHeading";
import { Eyebrow } from "./Eyebrow";
import styles from "./Section.module.css";

interface Props {
  children: ReactNode;
  eyebrow?: string;
  /** Usually a string. `FixturePage.tsx` passes a fragment of team name +
      decorative `ClubSwatch` + team name for its "Team A v Team B" heading —
      `DisplayHeading`'s own `children` is already `ReactNode`, so widening
      this to match costs nothing and the truthiness check below (`eyebrow ??
      heading`) still works for a non-empty fragment. */
  heading?: ReactNode;
  /**
   * Heading level for `heading`. Defaults to 2, because the common case is a
   * band on a page that already has an h1 above it.
   *
   * Pass 1 when this Section IS the page. Most routes are a single Section, so
   * without this their whole document outline starts at h2 and the page has no
   * h1 at all — which is the state every route except the landing page was in
   * before 2026-08-06.
   */
  level?: 1 | 2 | 3;
  /** §01 alternating wells. */
  tone?: "ink" | "deep";
  id?: string;
  /**
   * Give this Section's head the landing hero's scroll transformation.
   *
   * OPT-IN, and it has to stay opt-in. Landing stacks six of these as bands
   * mid-page, and every one of them would read `window.scrollY` and animate
   * from a progress value that has nothing to do with where the band is — the
   * ones below the fold would already be at rest before they were ever seen.
   * A hero is a page's FIRST element, and only the caller knows whether it is.
   *
   * Landing and information pages only (Ned, 2026-08-06): pricing, the legal
   * documents, the geo-refusal page. Never inside /app — a heading that moves
   * while you read a number is the wrong instinct in a tool.
   */
  heroScroll?: boolean;
}

export function Section({
  children,
  eyebrow,
  heading,
  level = 2,
  tone = "ink",
  id,
  heroScroll = false,
}: Props) {
  const headingId = id ? `${id}-heading` : undefined;
  /* Hooks cannot be conditional, so this always runs and the ref is simply not
     attached when heroScroll is false. The hook's effect returns early on a
     null ref, so an unattached one costs a closure and nothing else. */
  const heroRef = useScrollHero<HTMLDivElement>(280);

  return (
    <section className={`${styles.section} ${styles[tone]}`} id={id} aria-labelledby={headingId}>
      <Container>
        {(eyebrow ?? heading) && (
          <div
            className={`${styles.head} ${heroScroll ? styles.heroScroll : ""}`}
            ref={heroScroll ? heroRef : undefined}
          >
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            {heading && (
              <DisplayHeading as={`h${level}`} size="md" id={headingId}>
                {heading}
              </DisplayHeading>
            )}
          </div>
        )}
        {children}
      </Container>
    </section>
  );
}
